import type {
  EditableVariant,
  ProductFormData,
  ProductFormErrors,
  ProductPriceType,
} from './productFormTypes';

/**
 * Pure validation extracted from NewProduct.tsx. Semantics are intentionally
 * identical to the pre-refactor screen; regression tests capture them.
 */

const BARCODE_PATTERN = /^[A-Za-z0-9]+$/;

export type ProductFormValidationResult =
  | { valid: true }
  | {
      valid: false;
      fieldErrors: ProductFormErrors;
      formError?: string;
    };

export const getActiveVariantRows = (
  variantRows: EditableVariant[],
): EditableVariant[] => variantRows.filter(variant => !variant._softDeleted);

/**
 * Mirrors the `variantValidationError` memo: returns the first validation
 * message for the variant editor, or null when the variant path is valid.
 */
export const getVariantValidationError = (params: {
  hasVariants: boolean;
  variantAttributeNames: string[];
  activeVariantRows: EditableVariant[];
}): string | null => {
  const { hasVariants, variantAttributeNames, activeVariantRows } = params;
  if (!hasVariants) return null;
  if (variantAttributeNames.length === 0) {
    return 'Add at least one variant attribute';
  }
  if (activeVariantRows.length === 0) {
    return 'Add at least one active variant';
  }
  for (let index = 0; index < activeVariantRows.length; index += 1) {
    const variant = activeVariantRows[index];
    const lineLabel = `Variant ${index + 1}`;
    const sellingPrice = Number(variant.bp_variant_selling_price ?? 0);
    const buyingPrice = Number(variant.bp_variant_buying_price ?? 0);
    const quantity = Number(variant.bp_variant_quantity ?? 0);
    const minStock = Number(variant.bp_variant_minimum_stock_level ?? 0);
    if (!Number.isFinite(sellingPrice) || sellingPrice <= 0) {
      return `${lineLabel}: selling price must be greater than 0`;
    }
    if (!Number.isFinite(buyingPrice) || buyingPrice < 0) {
      return `${lineLabel}: buying price must be 0 or greater`;
    }
    if (!Number.isFinite(quantity) || quantity < 0) {
      return `${lineLabel}: quantity must be 0 or greater`;
    }
    if (!Number.isFinite(minStock) || minStock < 0) {
      return `${lineLabel}: minimum stock must be 0 or greater`;
    }
    const barcode = String(variant.bp_variant_barcode || '').trim();
    if (barcode && (barcode.length < 8 || !BARCODE_PATTERN.test(barcode))) {
      return `${lineLabel}: barcode must be alphanumeric and at least 8 characters`;
    }
    for (const attributeName of variantAttributeNames) {
      const attributeValue = String(
        variant.bp_variant_attributes?.[attributeName] || '',
      ).trim();
      if (!attributeValue) {
        return `${lineLabel}: ${attributeName} is required`;
      }
    }
  }
  return null;
};

/** Mirrors the `barcodeInvalid` memo. */
export const isBarcodeInvalid = (
  barcode: string | undefined,
  hasVariants: boolean,
): boolean => {
  if (hasVariants) return false;
  const code = (barcode || '').trim();
  if (!code) return false;
  return code.length < 8 || !BARCODE_PATTERN.test(code);
};

/** Mirrors the `itemMassRequiresUnit` memo. */
export const doesItemMassRequireUnit = (params: {
  priceType: ProductPriceType | string;
  mass: string | undefined;
  unitofmass: string | undefined;
}): boolean => {
  const { priceType, mass, unitofmass } = params;
  if (priceType !== 'item') return false;
  const massStr = String(mass ?? '').trim();
  if (!massStr) return false;
  const massNum = Number(massStr);
  if (!Number.isFinite(massNum) || massNum <= 0) return false;
  return !String(unitofmass ?? '').trim();
};

/**
 * Mirrors `validateForm` in NewProduct.tsx. Field errors match the existing
 * messages exactly; the variant path surfaces a single form-level error.
 */
export const validateProductForm = (params: {
  formData: ProductFormData;
  priceType: ProductPriceType | string;
  hasVariants: boolean;
  variantValidationError: string | null;
  /** The Service segment: name, category and a price; the cost is optional. */
  isService?: boolean;
}): ProductFormValidationResult => {
  const {
    formData,
    priceType,
    hasVariants,
    variantValidationError,
    isService = false,
  } = params;
  const newErrors: ProductFormErrors = {};

  if (!formData.productname) {
    newErrors.productname = 'Product name is required';
  }

  if (!formData.category) {
    newErrors.category = 'Category is required';
  }

  // Create forms no longer collect mass/unit for sell-per-item products
  // (size belongs in the product name). Only sell-per-unit still requires unit.
  // doesItemMassRequireUnit remains for edit/legacy screens that still show
  // mass fields when an existing product already has values.
  if (priceType === 'unit' && !formData.unitofmass) {
    newErrors.unitofmass = 'Unit of mass is required';
  }

  if (isService) {
    // A service keeps no stock: no quantity, minimum level or barcode.
    if (!(Number(formData.sellingprice) > 0)) {
      newErrors.sellingprice = 'Selling price must be greater than 0';
    }
    if (!isServiceCostValid(formData.buyingprice)) {
      newErrors.buyingprice = 'Cost must be 0 or greater';
    }
  } else if (hasVariants) {
    if (variantValidationError) {
      return {
        valid: false,
        fieldErrors: newErrors,
        formError: variantValidationError,
      };
    }
  } else {
    // Quantity may start at 0 (out of stock), but must be a real number:
    // a lone "." parses to NaN and must not pass.
    const qtyStr = String(formData.quantity ?? '').trim();
    const qty = Number(qtyStr);
    if (!qtyStr || !Number.isFinite(qty) || qty < 0) {
      newErrors.quantity = 'Quantity must be 0 or greater';
    }

    // Optional MSL: if provided, must be >= 0. Qty may sit below MSL
    // (sales drive stock under the alert threshold).
    const mslStr = String(formData.msl ?? '').trim();
    if (mslStr.length > 0) {
      const msl = Number(mslStr);
      if (!Number.isFinite(msl) || msl < 0) {
        newErrors.msl = 'Minimum stock level must be 0 or greater';
      }
    }

    if (!(Number(formData.buyingprice) > 0)) {
      newErrors.buyingprice = 'Buying price must be greater than 0';
    }

    if (!(Number(formData.sellingprice) > 0)) {
      newErrors.sellingprice = 'Selling price must be greater than 0';
    }

    // Optional barcode: if provided, min length 8 and alphanumeric only
    const trimmedBarcode = (formData.barcode || '').trim();
    if (trimmedBarcode) {
      if (trimmedBarcode.length < 8) {
        newErrors.barcode = 'Barcode must be at least 8 characters';
      } else if (!BARCODE_PATTERN.test(trimmedBarcode)) {
        newErrors.barcode = 'Barcode must contain only letters and numbers';
      }
    }
  }

  if (Object.keys(newErrors).length === 0) {
    return { valid: true };
  }
  return { valid: false, fieldErrors: newErrors };
};

/** A service's cost may be left blank; once typed it must be 0 or more. */
export const isServiceCostValid = (cost: string | undefined): boolean => {
  const costStr = String(cost ?? '').trim();
  if (!costStr) return true;
  const value = Number(costStr);
  return Number.isFinite(value) && value >= 0;
};

/** The submit button's `disabled` expression for NewProduct.tsx. */
export const isSubmitDisabled = (params: {
  clicked: boolean;
  isImageProcessing: boolean;
  formData: ProductFormData;
  priceType: ProductPriceType | string;
  hasVariants: boolean;
  variantValidationError: string | null;
  isService?: boolean;
}): boolean => {
  const {
    clicked,
    isImageProcessing,
    formData,
    priceType,
    hasVariants,
    variantValidationError,
    isService = false,
  } = params;
  return (
    clicked ||
    isImageProcessing ||
    !formData.category ||
    !formData.productname ||
    (priceType === 'unit' && !formData.unitofmass) ||
    (isService
      ? !(Number(formData.sellingprice) > 0) ||
        !isServiceCostValid(formData.buyingprice)
      : hasVariants
        ? Boolean(variantValidationError)
        : !formData.quantity ||
          !Number.isFinite(Number(formData.quantity)) ||
          !(Number(formData.buyingprice) > 0) ||
          !(Number(formData.sellingprice) > 0) ||
          isBarcodeInvalid(formData.barcode, hasVariants))
  );
};
